import {StyleSheet} from 'react-native';

// Reuses the same dark/glass palette introduced for the rules-engine screens
// (src/style/rulesEngine.js) so this screen doesn't invent a second theme. A follow-up
// design pass (per the port spec) will apply GPX's finished component library once this
// screen's logic is verified correct.
export const colors = {
  primary: '#336599',
  primaryDark: '#253c57',
  accent: '#1CBB77',
  accentLight: '#7BFAC3',
  text: '#ffffff',
  textMuted: '#8eacc4',
  danger: '#e74c3c',
  dangerLight: '#4a1a1a',
  cardBg: '#1e3048',
  inputBg: '#162436',
  border: 'rgba(255,255,255,0.15)',
  borderStrong: 'rgba(255,255,255,0.25)',
  savingGreen: '#1CBB77',
  costRed: '#e74c3c'
};

const styles = StyleSheet.create({
  screen: {
    flex: 1
  },
  scrollContent: {
    paddingTop: 32,
    paddingHorizontal: 20,
    paddingBottom: 60,
    alignItems: 'center'
  },
  panel: {
    width: '100%',
    maxWidth: 900,
    borderRadius: 20,
    padding: 24,
    backgroundColor: 'rgba(20, 33, 48, 0.6)',
    borderWidth: 1,
    borderColor: colors.border
  },
  headline: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 24,
    color: colors.text,
    marginBottom: 6,
    textAlign: 'center'
  },
  subhead: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: colors.textMuted,
    marginBottom: 12,
    textAlign: 'center'
  },
  heroDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginBottom: 28,
    width: '60%',
    alignSelf: 'center'
  },

  // Each top-level step gets its own clearly bounded card — a distinct background tone,
  // a colored top accent stripe (matching its numbered badge), border, radius, and drop
  // shadow — so a first-time visitor can see at a glance where one section ends and the
  // next begins, instead of one continuous scroll of fields. The background tone is
  // deliberately lighter than the subsection cards nested inside "Tangible Costs" so the
  // two levels of hierarchy read as distinct even against the screen's own dark gradient.
  section: {
    marginBottom: 36,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderTopWidth: 4,
    borderTopColor: colors.primary,
    padding: 20,
    boxShadow: '0px 8px 28px rgba(0,0,0,0.35)'
  },

  // Section header: numbered badge + title + one-line "what is this for" description,
  // replacing the old plain bold-text-with-underline treatment so headers read as
  // headers rather than inline emphasis.
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 18,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  sectionBadge: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    flexShrink: 0
  },
  sectionBadgeText: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 15,
    color: colors.text
  },
  sectionHeaderText: {
    flex: 1,
    minWidth: 0
  },
  sectionTitle: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 18,
    color: colors.text
  },
  sectionDescription: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 13,
    color: colors.textMuted,
    marginTop: 4,
    lineHeight: 18
  },

  // Nested subsection card used inside "Tangible Costs" (Communication, Replacement
  // Shipment, Return/Abandonment) — a lighter inset panel with its own small numbered
  // badge so each subsection reads as a distinct group without competing visually with
  // the top-level section cards.
  subsectionCard: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 3,
    borderLeftColor: colors.accent,
    padding: 16,
    marginBottom: 16
  },
  subsectionHeaderRow: {
    marginBottom: 12,
    paddingBottom: 0,
    borderBottomWidth: 0
  },
  subsectionBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: colors.accent,
    marginRight: 10
  },
  subsectionBadgeText: {
    fontSize: 11,
    color: colors.accentLight
  },
  subsectionTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    color: colors.accentLight
  },
  subsectionDescription: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 2,
    lineHeight: 16
  },
  sectionSubtitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    color: colors.accentLight,
    marginTop: 12,
    marginBottom: 8
  },

  fieldRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginBottom: 10
  },
  field: {
    minWidth: 150,
    flexGrow: 1,
    flexBasis: 0,
    overflow: 'hidden',
    maxWidth: 320
  },
  fieldLabel: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: colors.textMuted,
    marginBottom: 4
  },
  // Same treatment as SectionHeader's description text (subsectionDescription scale, since
  // a field-level caption sits below section/subsection level), for a field that needs more
  // explanation than the label alone can carry.
  fieldDescription: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    color: colors.textMuted,
    marginBottom: 4,
    lineHeight: 16
  },
  input: {
    backgroundColor: colors.inputBg,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: 40,
    width: '100%',
    boxSizing: 'border-box',
    color: colors.text,
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.border
  },
  reciprocalHint: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 11,
    color: colors.textMuted,
    marginTop: 2
  },

  inputWithPrefix: {
    position: 'relative',
    justifyContent: 'center',
    width: '100%'
  },
  inputPrefixText: {
    position: 'absolute',
    left: 12,
    zIndex: 1,
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: colors.textMuted
  },
  inputWithPrefixPadding: {
    paddingLeft: 22
  },

  costBox: {
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    padding: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: colors.border,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  costBoxLabel: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: colors.text,
    flexShrink: 1,
    marginRight: 12
  },
  costBoxValue: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 18,
    color: colors.text
  },

  totalsPanel: {
    marginTop: 8,
    borderRadius: 12,
    padding: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: colors.borderStrong
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  totalsRowLast: {
    borderBottomWidth: 0
  },
  totalsLabel: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    color: colors.text
  },
  totalsValue: {
    fontFamily: 'Poppins_700Bold',
    fontSize: 18,
    color: colors.text
  },
  totalsValueCost: {
    color: colors.costRed
  },
  totalsValueSaving: {
    color: colors.savingGreen,
    fontSize: 22
  },

  inlineErrorText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 11,
    color: '#f5a89e',
    lineHeight: 16
  },

  teamPanel: {
    marginTop: 8
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginVertical: 10
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkboxChecked: {
    backgroundColor: colors.accent
  },
  checkboxLabel: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 13,
    color: colors.text
  },
  textArea: {
    backgroundColor: colors.inputBg,
    borderRadius: 6,
    padding: 12,
    minHeight: 90,
    color: colors.text,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.border,
    textAlignVertical: 'top'
  },

  sendButton: {
    backgroundColor: colors.accent,
    borderRadius: 8,
    height: 46,
    paddingHorizontal: 28,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    marginTop: 12
  },
  sendButtonDisabled: {
    opacity: 0.6
  },
  sendButtonText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 15
  },

  errorBanner: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    backgroundColor: colors.dangerLight,
    borderWidth: 1,
    borderColor: colors.danger,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  errorBannerText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.text,
    fontSize: 13,
    flex: 1,
    marginRight: 12
  },
  retryButton: {
    backgroundColor: colors.accent,
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 8
  },
  retryButtonText: {
    fontFamily: 'Poppins_600SemiBold',
    color: colors.text,
    fontSize: 12
  },

  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60
  },
  loadingText: {
    fontFamily: 'Poppins_500Medium',
    color: colors.textMuted,
    fontSize: 14,
    marginTop: 12
  },

  ctaDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginTop: 8,
    marginBottom: 24
  },
  ctaIntro: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    marginBottom: 4
  },
  ctaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 6,
    justifyContent: 'center'
  },
  ctaButton: {
    borderRadius: 8,
    paddingHorizontal: 26,
    paddingVertical: 14,
    minWidth: 180,
    alignItems: 'center'
  },
  ctaPrimary: {
    backgroundColor: colors.accent
  },
  ctaSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: colors.primary
  },
  ctaPrimaryText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 16
  },
  ctaSecondaryText: {
    fontFamily: 'Poppins_700Bold',
    color: colors.text,
    fontSize: 16
  }
});

export default styles;
